import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, HardHat } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DailyReportSwitch } from "@/components/DailyReportSwitch";
import { LunchFields } from "@/components/LunchFields";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { ComuneInput, StreetInput } from "@/components/booking/AddressFields";
import { LocationPicker } from "@/components/booking/LocationPicker";
import { MultiDatePicker, shortDay } from "@/components/MultiDatePicker";
import { HelpersPicker } from "@/components/HelpersPicker";
import { RecurringFields } from "@/components/RecurringFields";
import { todayISO } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CantiereTypePicker } from "@/components/CantiereTypePicker";

const EMPTY = { name: "", site_type: "", committente: "", referente_phone: "", notes: "", address: "", city: "", lat: null, lng: null,
  dates: [], start: "08:00", end: "17:00", technician_id: "", helper_ids: [], shifts: [], daily_report: true, lunch_start: null, lunch_end: null };

const Block = ({ title, children }) => (
  <section className="rounded-2xl border bg-white p-5 sm:p-6"><h2 className="mb-4 font-display text-lg font-semibold">{title}</h2>{children}</section>
);

export default function NewCantiere() {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [techs, setTechs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [conf, setConf] = useState(null);
  const [mode, setMode] = useState("flex");
  const [rec, setRec] = useState({ weekdays: [0, 1, 2, 3, 4, 5], shifts: [{ start: "09:00", end: "13:00" }], start_date: todayISO() });
  useEffect(() => {
    Promise.all([api.get("/admin/technicians"), api.get("/admin/technicians", { params: { role: "responsabile" } })])
      .then(([a, b]) => setTechs([...a.data, ...b.data.map((r) => ({ ...r, name: `${r.name} (resp. ufficio)` }))].filter((t) => t.active)));
  }, []);
  useEffect(() => {
    setF((p) => {
      const kept = p.shifts.filter((s) => p.dates.includes(s.date));
      const add = p.dates.filter((d) => !kept.some((s) => s.date === d)).map((d) => ({ key: `${d}-${Math.random()}`, date: d, start: p.start, end: p.end }));
      return { ...p, shifts: [...kept, ...add].sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start)) };
    });
  }, [f.dates]);
  const setShift = (key, k, v) => setF((p) => ({ ...p, shifts: p.shifts.map((s) => (s.key === key ? { ...s, [k]: v } : s)) }));
  const addShift = (date) => setF((p) => ({ ...p, shifts: [...p.shifts, { key: `${date}-${Math.random()}`, date, start: "14:00", end: "18:00" }].sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start)) }));
  const delShift = (key) => setF((p) => ({ ...p, shifts: p.shifts.filter((s) => s.key !== key) }));
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pickType = (t) => {
    setF((p) => ({ ...p, site_type: t?.name || "", ...(t?.mode === "flex" ? { start: t.start, end: t.end, shifts: p.shifts.map((s) => ({ ...s, start: t.start, end: t.end })) } : {}) }));
    if (!t) return;
    setMode(t.mode);
    if (t.mode === "recur") setRec((r) => ({ ...r, weekdays: t.weekdays, shifts: t.shifts, day_shifts: {} }));
    setF((p) => ({ ...p, daily_report: t.mode !== "recur" }));
  };
  const submit = async () => {
    if (f.name.trim().length < 2) return toast.error("Inserisci il nome del cantiere");
    if (f.address.trim().length < 3) return toast.error("Inserisci l'indirizzo del cantiere");
    if (mode === "flex" && !f.dates.length) return toast.error("Seleziona almeno un giorno");
    if (mode === "recur" && !rec.weekdays.length) return toast.error("Scegli almeno un giorno della settimana");
    setBusy(true); setConf(null);
    try {
      const helper_ids = f.technician_id ? f.helper_ids.filter((h) => h !== f.technician_id) : [];
      const sched = mode === "recur" ? { dates: [], shifts: [], recurring: rec } : { shifts: f.shifts.map(({ date, start, end }) => ({ date, start, end })) };
      await api.post("/admin/cantieri", { ...f, technician_id: f.technician_id || null, helper_ids, ...sched });
      toast.success(mode === "recur" ? `Cantiere "${f.name.trim()}" creato con orario ripetitivo` : `Cantiere "${f.name.trim()}" creato su ${f.dates.length} giorni`);
      nav("/admin/cantieri");
    } catch (e) {
      if (e?.response?.status === 409) setConf(e.response.data.detail); else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="max-w-4xl space-y-5" data-testid="new-cantiere-page">
      <PageHeader eyebrow="Cantieri" title="Nuovo cantiere" />
      <Block title="Dati del cantiere">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2"><Label>Nome cantiere *</Label><Input value={f.name} onChange={upd("name")} placeholder="Es. Villa Bianchi – impianto radiante" data-testid="cantiere-name-input" /></div>
          <CantiereTypePicker value={f.site_type} onPick={pickType} />
          <div className="space-y-2"><Label>Committente</Label><Input value={f.committente} onChange={upd("committente")} placeholder="Nome o azienda" data-testid="cantiere-committente-input" /></div>
          <div className="space-y-2"><Label>Telefono referente in cantiere</Label><Input type="tel" value={f.referente_phone} onChange={upd("referente_phone")} data-testid="cantiere-phone-input" /></div>
          <div className="space-y-2"><Label>Comune</Label>
            <ComuneInput value={f.city} onChange={(v) => setF((p) => ({ ...p, city: v }))} onSelect={(c) => setF((p) => ({ ...p, city: c.name }))} /></div>
          <div className="space-y-2"><Label>Indirizzo *</Label>
            <StreetInput value={f.address} comune={f.city} onChange={(v) => setF((p) => ({ ...p, address: v }))}
              onPick={(s) => setF((p) => ({ ...p, address: [s.street || s.name, s.housenumber].filter(Boolean).join(" "), city: s.city || p.city, lat: s.lat, lng: s.lng }))} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Posizione sulla mappa</Label>
            <LocationPicker lat={f.lat} lng={f.lng} address={f.address} city={f.city} onChange={({ geo_source, ...patch }) => setF((p) => ({ ...p, ...patch }))} /></div>
          <div className="space-y-2 sm:col-span-2"><Label>Note</Label><Textarea rows={3} value={f.notes} onChange={upd("notes")} placeholder="Accessi, materiali, indicazioni per la squadra" data-testid="cantiere-notes-input" /></div>
          <DailyReportSwitch checked={f.daily_report} onChange={(v) => setF((p) => ({ ...p, daily_report: v }))} testid="cantiere-daily-report-switch" />
          <LunchFields start={f.lunch_start} end={f.lunch_end} onChange={(v) => setF((p) => ({ ...p, ...v }))} testid="cantiere-lunch" className="sm:col-span-2" />
        </div>
      </Block>
      <Block title="Giorni e fasce orarie">
        <Tabs value={mode} onValueChange={(m) => { setMode(m); setF((p) => ({ ...p, daily_report: m !== "recur" })); }} className="mb-4">
          <TabsList>
            <TabsTrigger value="flex" data-testid="cantiere-mode-flex">Orario flessibile</TabsTrigger>
            <TabsTrigger value="recur" data-testid="cantiere-mode-recur">Orario ripetitivo</TabsTrigger>
          </TabsList>
        </Tabs>
        {mode === "recur" ? <RecurringFields value={rec} onChange={setRec} /> : (<>
        <MultiDatePicker value={f.dates} onChange={(dates) => setF((p) => ({ ...p, dates }))} />
        <div className="mt-4 grid max-w-sm grid-cols-2 gap-3">
          <div className="space-y-1"><Label>Orario predefinito dalle</Label><Input type="time" value={f.start} onChange={upd("start")} data-testid="cantiere-start-input" /></div>
          <div className="space-y-1"><Label>Alle</Label><Input type="time" value={f.end} onChange={upd("end")} data-testid="cantiere-end-input" /></div>
        </div>
        {f.shifts.length > 0 && (
          <div className="mt-5 space-y-2" data-testid="cantiere-shifts">
            <p className="text-sm font-medium">Fasce orarie per giorno</p>
            {f.shifts.map((s, i) => (
              <div key={s.key} className="flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2" data-testid={`shift-row-${i}`}>
                <span className="w-28 text-sm capitalize">{shortDay(s.date)}</span>
                <Input type="time" value={s.start} onChange={(e) => setShift(s.key, "start", e.target.value)} className="w-28" data-testid={`shift-start-${i}`} />
                <span className="text-slate-400">–</span>
                <Input type="time" value={s.end} onChange={(e) => setShift(s.key, "end", e.target.value)} className="w-28" data-testid={`shift-end-${i}`} />
                <Button type="button" size="sm" variant="ghost" onClick={() => addShift(s.date)} data-testid={`shift-add-${i}`}>+ turno</Button>
                {f.shifts.filter((x) => x.date === s.date).length > 1 && <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={() => delShift(s.key)} data-testid={`shift-remove-${i}`}>Rimuovi</Button>}
              </div>
            ))}
            <p className="text-xs text-slate-500">Ogni turno compare nell'agenda come appuntamento separato. Dopo la creazione puoi assegnare a ogni turno una squadra diversa dal dettaglio del cantiere.</p>
          </div>
        )}
        </>)}
      </Block>
      <Block title="Squadra">
        <div className="space-y-4">
          <div className="space-y-2"><Label>Responsabile (compila i rapportini)</Label>
            <select value={f.technician_id} onChange={(e) => setF((p) => ({ ...p, technician_id: e.target.value, helper_ids: p.helper_ids.filter((h) => h !== e.target.value) }))}
              className="w-full rounded-lg border bg-white px-3 py-2 text-sm sm:max-w-sm" data-testid="cantiere-lead-select">
              <option value="">Nessuno, assegno più tardi</option>
              {techs.map((t) => <option key={t.id} value={t.id}>{t.name}{t.employment === "autonomo" ? " (autonomo)" : ""}</option>)}
            </select></div>
          <HelpersPicker techs={techs} leadId={f.technician_id} value={f.helper_ids} onChange={(helper_ids) => setF((p) => ({ ...p, helper_ids }))} />
        </div>
      </Block>
      {conf && <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="cantiere-conflict-alert"><p className="font-semibold">{conf.message}</p><ul className="mt-1 list-disc pl-5">{conf.conflicts?.map((c, i) => <li key={i}>{c.message}</li>)}</ul></div>}
      <Button onClick={submit} disabled={busy} className="h-12 rounded-full px-8" data-testid="cantiere-save-button">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <HardHat className="h-4 w-4" />} Crea cantiere
      </Button>
    </div>
  );
}
